import { Button, Tag, Tooltip } from 'antd';
import React from 'react';
import MiddleEllipsis from 'components/MiddleEllipsis';
import { renderDate, renderDateTime } from 'components/Table/renderers';
import { automationPotential, controlStatus, syncStatus } from './tags';
import {
  SCAN_TYPE,
  allIssueColumnKeys,
  applicablePageTypesLabel,
  findingOccurrenceIds,
  findingReviewPresentation,
  latestScanPresentation,
  pageColumnKeys,
  pageTypeLabel,
  pagesCheckedPresentation,
  pdfBatchProgressPresentation,
  scanReasonTriggerPresentation,
  scanStatusPresentation,
  scanTypeIncludes,
} from './viewModel';
import { FindingDispositionSelect } from './FindingDispositionSelect';
import { WcagCriterionHelp } from './WcagCriterionHelp';

const compactTextStyle = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const requirementCellStyle = {
  minWidth: 0,
};

const requirementTextStyle = {
  ...compactTextStyle,
  fontWeight: 500,
  minWidth: 0,
};

const requirementContextStyle = {
  ...compactTextStyle,
  color: 'rgba(0, 0, 0, 0.45)',
  fontSize: 12,
  marginTop: 4,
  minWidth: 0,
};

const reviewPrimaryStyle = {
  alignItems: 'flex-start',
  display: 'flex',
  gap: 4,
  minWidth: 0,
};

const reviewTagsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginTop: 4,
};

const tagStyle = { marginInlineEnd: 0 };

const renderReviewSummary = ({ primary, status, automation, context, level, criterionIds }) => (
  <div style={requirementCellStyle}>
    <div style={reviewPrimaryStyle}>
      <Tooltip title={primary}><span style={requirementTextStyle}>{primary}</span></Tooltip>
      {level && <Tag style={{ ...tagStyle, flex: 'none' }}>{level}</Tag>}
    </div>
    {(status !== undefined || automation) && (
      <div style={reviewTagsStyle}>
        {status !== undefined && controlStatus(status, { style: tagStyle })}
        {automation && automationPotential(automation, { style: tagStyle })}
      </div>
    )}
    {context && <Tooltip title={context}><span style={requirementContextStyle}>{context}</span></Tooltip>}
    <WcagCriterionHelp compact criterionIds={criterionIds} />
  </div>
);

export const controlColumns = ({ onReviewManually, onViewIssues, reviewCoverageAccepted }) => [{
  title: 'Review',
  key: 'review',
  width: 280,
  render: (_, control) => renderReviewSummary({
    primary: `${control.id} ${control.title}`,
    level: control.level,
    status: control.status,
    automation: control.automationPotential,
    criterionIds: [control.id],
  }),
}, {
  title: 'Principle',
  dataIndex: 'principle',
  width: 105,
}, {
  title: 'Potentially applies to',
  key: 'applicablePageTypes',
  width: 110,
  render: (_, control) => applicablePageTypesLabel(control.applicablePageTypes),
}, {
  title: 'Scanner evidence',
  dataIndex: 'findingCount',
  width: 110,
  render: (count, control) => (count > 0
    ? <Button size="small" onClick={() => onViewIssues(control)}>{`View evidence (${count})`}</Button>
    : '—'),
}, {
  title: 'Manual coverage',
  key: 'manualCoverage',
  width: 135,
  render: (_, control) => (control.manualTargetCount
    ? `${control.manualReviewedCount} / ${control.manualTargetCount} representative pages`
    : 'Not set'),
}, {
  title: 'Manual review',
  key: 'manualReview',
  width: 120,
  render: (_, control) => (reviewCoverageAccepted
    ? <Button size="small" onClick={() => onReviewManually(control)}>Review</Button>
    : <Button size="small" onClick={() => onReviewManually(control)}>Guidance</Button>),
}];

const renderLastObserved = value => (value ? (
  <Tooltip title={renderDateTime(value)}>
    <span>{renderDate(value)}</span>
  </Tooltip>
) : '—');

export const issueTypeColumns = (onView, suiteType = 'Accessibility') => {
  const primaryColumns = suiteType === 'Accessibility' ? [{
    title: 'Review',
    key: 'requirement',
    width: 240,
    render: (_, group) => renderReviewSummary({
      ...findingReviewPresentation(group),
      criterionIds: group.criterionIds,
    }),
  }, {
    title: 'Provider rule',
    dataIndex: 'ruleId',
    width: 130,
    render: ruleId => <Tooltip title={ruleId}><span style={compactTextStyle}>{ruleId}</span></Tooltip>,
  }] : [{
    title: 'Finding type',
    dataIndex: 'ruleId',
    width: 240,
  }];
  return [...primaryColumns, {
    title: 'Pages',
    dataIndex: 'pageCount',
    type: 'number',
    width: 90,
  }, {
    title: 'Issues',
    dataIndex: 'occurrenceCount',
    type: 'number',
    width: 90,
  }, {
    title: 'Last observed',
    dataIndex: 'lastSeenAt',
    type: 'datetime',
    width: 110,
    render: renderLastObserved,
  }, {
    title: 'Changes',
    key: 'changes',
    width: 110,
    render: (_, group) => [
      group.newCount && <Tag key="new" color="blue">{`${group.newCount} new`}</Tag>,
      group.recurringCount && <Tag key="recurring">{`${group.recurringCount} recurring`}</Tag>,
      group.regressedCount && <Tag key="regressed" color="error">{`${group.regressedCount} regressed`}</Tag>,
    ].filter(Boolean),
  }, {
    title: 'Actions',
    key: 'view',
    width: 90,
    render: (_, group) => <Button size="small" onClick={() => onView(group)}>View</Button>,
  }];
};

const pageReferenceStyle = {
  ...compactTextStyle,
  maxWidth: 238,
};

const pageUrlWrapStyle = {
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
};

const renderPageUrl = (url) => {
  if (!url) return '—';
  return (
    <div style={pageUrlWrapStyle}>
      <MiddleEllipsis>
        <span className="constrainedChild">
          <Tooltip title={url}>
            <a href={url} target="_blank" rel="noopener noreferrer" className="ellipseMe">{url}</a>
          </Tooltip>
        </span>
      </MiddleEllipsis>
    </div>
  );
};

const renderPageReference = (_, occurrence) => {
  const { pageTitle, pageUrl } = occurrence;
  const label = pageTitle || pageUrl;
  if (!label) return '—';
  if (!pageUrl) return <Tooltip title={label}><span style={pageReferenceStyle}>{label}</span></Tooltip>;
  const tooltip = pageTitle && pageTitle !== pageUrl ? (
    <>
      <div>{pageTitle}</div>
      <div>{pageUrl}</div>
    </>
  ) : pageUrl;
  return (
    <Tooltip title={tooltip}>
      <a href={pageUrl} target="_blank" rel="noopener noreferrer" style={pageReferenceStyle}>{label}</a>
    </Tooltip>
  );
};

export const allIssueColumns = (onView, options = {}) => {
  const accessibility = options.suiteType === 'Accessibility';
  const byKey = {
    issueType: {
      title: accessibility ? 'Review' : 'Finding type',
      dataIndex: 'ruleId',
      width: accessibility ? 190 : 150,
      render: (value, record) => {
        const presentation = accessibility ? findingReviewPresentation(record) : null;
        return presentation
          ? renderReviewSummary({ ...presentation, criterionIds: record.criterionIds })
          : <Tooltip title={value}><span style={compactTextStyle}>{value}</span></Tooltip>;
      },
    },
    type: {
      title: 'Type',
      dataIndex: 'pageType',
      width: 72,
      render: pageType => pageTypeLabel(pageType),
    },
    providerRule: {
      title: 'Provider rule',
      dataIndex: 'ruleId',
      width: 130,
      render: ruleId => <Tooltip title={ruleId}><span style={compactTextStyle}>{ruleId}</span></Tooltip>,
    },
    page: {
      title: 'Page',
      key: 'page',
      ...(options.groupedByPage
        ? {
          minWidth: 0,
          width: '100%',
          onCell: () => ({ style: { overflow: 'hidden', maxWidth: 0 } }),
        }
        : { width: 270 }),
      render: (_, record) => renderPageUrl(record.pageUrl),
    },
    findings: {
      title: 'Findings',
      dataIndex: 'findingCount',
      type: 'number',
      width: 90,
    },
    status: {
      title: 'Status',
      key: 'status',
      width: 148,
      render: (_, record) => (
        <FindingDispositionSelect
          record={record}
          disabled={!options.canEditDisposition}
          loading={findingOccurrenceIds(record).some(id => options.dispositionLoadingIds?.has(id))}
          onChange={options.onDispositionChange}
        />
      ),
    },
    change: {
      title: 'Change',
      dataIndex: 'detectionState',
      width: 100,
      render: state => (state ? <Tag style={{ textTransform: 'capitalize' }}>{state.replace(/_/g, ' ')}</Tag> : '—'),
    },
    lastObserved: {
      title: 'Last observed',
      dataIndex: 'lastSeenAt',
      width: 110,
      render: renderLastObserved,
    },
    evidence: {
      title: '',
      key: 'view',
      width: 90,
      render: (_, record) => <Button size="small" onClick={() => onView(record.occurrence || record)}>Evidence</Button>,
    },
  };
  return allIssueColumnKeys(options).map(key => byKey[key]);
};

export const scanColumns = [{
  title: 'Source',
  dataIndex: 'source',
}, {
  title: 'Scan type',
  dataIndex: 'scanType',
}, {
  title: 'Reason/trigger',
  key: 'reasonTrigger',
  render: (_, row) => scanReasonTriggerPresentation(row),
}, {
  title: 'Started',
  dataIndex: 'startedAt',
  type: 'datetime',
}, {
  title: 'Completed',
  dataIndex: 'completedAt',
  type: 'datetime',
}, {
  title: 'Status',
  key: 'status',
  render: (_, row) => (row.historicalResults ? scanStatusPresentation(row) : syncStatus(row.status)),
}, {
  title: 'Pages checked',
  key: 'pagesChecked',
  render: (_, row) => pagesCheckedPresentation(row),
}, {
  title: 'Findings',
  dataIndex: 'findings',
  type: 'number',
}, {
  title: 'PDF batch progress',
  key: 'pdfBatchProgress',
  render: (_, row) => pdfBatchProgressPresentation(row) || (scanTypeIncludes(row.scanType, SCAN_TYPE.PDF_SCAN) && !row.historicalResults ? '—' : ''),
}];

const renderLatestScan = (page, options) => {
  const presentation = latestScanPresentation(page, options);
  if (presentation.kind === 'completed' && presentation.completedAt) return renderDate(presentation.completedAt);
  return presentation.text;
};

export const pageColumns = ({ onView, pdfScanningEnabled, pageType, scanHistory } = {}) => {
  const scanOptions = { pdfScanningEnabled, scanHistory };
  const byKey = {
    type: {
      title: 'Type',
      key: 'type',
      dataIndex: 'pageType',
      width: 72,
      render: value => pageTypeLabel(value),
    },
    page: {
      title: 'Page',
      key: 'page',
      width: 270,
      render: (_, page) => renderPageReference(null, {
        pageTitle: page.pageTitle,
        pageUrl: page.canonicalUrl,
      }),
    },
    issues: {
      title: 'Issues',
      key: 'issues',
      dataIndex: 'issueCount',
      type: 'number',
      defaultSortOrder: 'descend',
      width: 72,
    },
    linkedFrom: {
      title: 'Linked from',
      key: 'linkedFrom',
      dataIndex: 'linkedFromCount',
      type: 'number',
      width: 110,
    },
    latestScan: {
      title: 'Latest scan',
      key: 'latestScan',
      render: (_, page) => renderLatestScan(page, scanOptions),
    },
    latestPdfScanEvidence: {
      title: 'Latest PDF scan',
      key: 'latestPdfScanEvidence',
      render: (_, page) => renderLatestScan(page, scanOptions),
    },
    scanType: {
      title: 'Scan type',
      key: 'scanType',
      render: (_, page) => latestScanPresentation(page, scanOptions).scanType,
    },
    evidence: {
      title: 'Evidence',
      key: 'evidence',
      render: (_, page) => (
        <Button
          onClick={() => onView(page)}
          aria-label={`Evidence for ${page.canonicalUrl || page.pageUrl || 'page'}`}
        >
          Evidence
        </Button>
      ),
    },
  };
  return pageColumnKeys(pageType).map(key => byKey[key]);
};
